import {useState, useEffect} from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'

import Chip from '@mui/material/Chip'

import AuthenticatorAppIcon from '../AuthenticatorAppIcon'
import BackupCodeIcon from '../BackupCodeIcon'
import {Box, BoxGutterMedium} from '../../../../dashboard/src/components/blocks/Boxes'
import {Button, Cancel} from '../../../../dashboard/src/components/blocks/Buttons'
import ButtonGroup from '../../../../shared_components/core/buttons/ButtonGroup'
import PhoneIcon from './PhoneIcon'
import {Text, TextGutterMedium} from '../../../../dashboard/src/components/blocks/Texts'
import Loader from '../../../../shared_components/NewLoader'

import preAuthHook from '../../lib/preAuthHook'
import routerUtils from '../../../../lib/routerUtils'
import SignInActions from '../../actions/SignInActions'
import SessionStore from '../../stores/SessionStore'

import './signInAlternatives.scss'

const SignInAlternative = props => {
  const handleClick = () => {
    // App will always be primary mode if present
    if (props.primaryEnrollment?.mode === props.mode || props.mode !== 'phone')
      props.selectMode(props.mode)
    else
      props.selectMode(`primary_${props.mode}`)
  }

  return (
    <BoxGutterMedium className={classnames('alternative-option', 'flex-container', {selected: props.selected})} onClick={handleClick}>
      <Text className='icon'>
        {props.icon}
      </Text>
      <Text className='details'>
        <Text className='heading flex-container'>
          <h5>{props.label}</h5>
          {props.primary && <Chip className='badge' label='Primary' size='small' />}
        </Text>
        <Text className='option-description'>{props.description}</Text>
      </Text>
    </BoxGutterMedium>
  )
}

const SignInAlternatives = () => {
  const [loading, setLoading] = useState(false)
  const [mode, setMode] = useState('')
  const [altSignIn, setAltSignIn] = useState('')

  const sessionState = SessionStore.getState()

  useEffect(() => {
    preAuthHook()
  }, [])

  const cancel = () => {
    if (!routerUtils.goBack())
      routerUtils.push('auth/sign_in/tfa_primary')
  }

  const setTfaMode = value => {
    setMode(value)

    switch (value) {
      case 'app':
      case 'phone':
        setAltSignIn('tfa_primary')
        break
      case 'recovery':
        setAltSignIn('tfa_backup')
        break
      case 'primary_phone':
      case 'secondary':
        setAltSignIn('tfa_secondary')
        break
      default:
        break
    }
  }

  const goToTfaSecondary = () => {
    SessionStore.resetForSecondaryPhoneSignIn()
    routerUtils.push('auth/sign_in/tfa_secondary')
  }

  const handleSubmit = () => {
    const email = SessionStore.getState().preAuthEmail
    const password = SessionStore.getState().preAuthPassword

    switch (altSignIn) {
      case 'tfa_primary':
        setLoading(true)
        SignInActions.signIn(email, password)
        break
      case 'tfa_secondary':
        setLoading(true)
        mode === 'primary_phone' ?
          SignInActions.signInPrimaryPhone({email, password, phoneId: sessionState.primaryPhone?.id}) :
          goToTfaSecondary()
        break
      case 'tfa_backup':
        routerUtils.push('auth/sign_in/tfa_backup')
        break
      default:
        break
    }
  }

  return (
    <Loader loading={loading}>
      <Box className='sign-in-alternatives'>
        <h4>Try a different method</h4>
        <TextGutterMedium className='subtitle'>
          Choose an alternative verification method
        </TextGutterMedium>
        {
          sessionState.enrollments?.sort(
            currentEnrollment => (currentEnrollment.category === 'primary' ? -1 : 1)
          )?.map(
            enrollment => (
              <SignInAlternative
                key={enrollment.id}
                label={enrollment.name}
                icon={enrollment.mode === 'app' ? <AuthenticatorAppIcon /> : <PhoneIcon />}
                description={enrollment.mode === 'app' ? 'Use your authentication app' : 'Receive a code via text or automated voice call'}
                primary={enrollment.category === 'primary'}
                mode={enrollment.mode}
                selected={mode === enrollment.mode || mode.includes(enrollment.mode)}
                selectMode={setTfaMode}
                primaryEnrollment={sessionState.primaryEnrollment}
              />
            )
          )
        }
        {
          sessionState.secondaryPhone && (
            <SignInAlternative
              label='Secondary phone number'
              icon={<PhoneIcon />}
              description='Receive a code via text or automated voice call to your backup phone number'
              mode='secondary'
              selected={mode === 'secondary'}
              selectMode={setTfaMode}
            />
          )
        }
        <SignInAlternative
          label='Backup code'
          icon={<BackupCodeIcon />}
          description='Use one of your backup codes'
          mode='recovery'
          selected={mode === 'recovery'}
          selectMode={setTfaMode}
        />
        <ButtonGroup>
          <Cancel onClick={cancel} />
          <Button disabled={!mode} onClick={handleSubmit} processing={loading}>Next</Button>
        </ButtonGroup>
      </Box>
    </Loader>
  )
}

SignInAlternative.propTypes = {
  description: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  mode: PropTypes.string,
  primary: PropTypes.bool,
  selected: PropTypes.bool,
  selectMode: PropTypes.func,
  primaryEnrollment: PropTypes.object
}

export default SignInAlternatives
